import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import leaf from '../../assets/images/leaf.svg';
import Pagination from '../../components/Pagination';
import Button from '../../components/Button';
import BuyerInquiryService from '../../api/service/buyer/BuyerInquiryService';
import {
  InquiryData,
  InquiryListData,
} from '../../api/service/InquiryInterfaces';
import SellerInquiryService from '../../api/service/seller/SellerInquiryService';
import { formatDateWithTime } from '../../utils/formatDateWithTime';

const NoticeTabWrapper = styled.div``;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
  img {
    width: 20px;
  }
`;

const TableContainer = styled.div`
  min-height: 481px;
  overflow-x: auto;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
`;

const TableBox = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: white;

  tr {
    border-bottom: 1px solid #ddd;
  }

  tr:nth-child(12) {
    border-bottom: none; /* 마지막 행의 border 제거 */
  }

  th,
  td {
    font-size: 16px;
    padding: 10px;
    text-align: center;
    vertical-align: middle; /* 세로 정렬 */
    white-space: nowrap; /* 줄바꿈 방지 */
  }

  th {
    background-color: #85c1a9;
    color: white;
    font-weight: 500;
  }

  th:first-child {
    border-top-left-radius: 10px;
  }

  th:last-child {
    border-top-right-radius: 10px;
  }

  /* 제목 열을 가장 넓게 설정 */
  th:nth-child(4),
  td:nth-child(4) {
    width: 50%; /* 제목 열의 폭을 설정 */
  }

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;

    tr {
      border-bottom: 1px solid #ddd;
    }

    th,
    td {
      font-size: 12px;
      padding: 10px;
      text-align: center;
      vertical-align: middle; /* 세로 정렬 */
      white-space: nowrap; /* 줄바꿈 방지 */
    }

    th {
      font-weight: 400;
    }

    th:first-child {
      border-top-left-radius: 0px;
    }

    th:last-child {
      border-top-right-radius: 0px;
    }

    /* 제목 열 폭 조정 */
    th:nth-child(4),
    td:nth-child(4) {
      width: 40%; /* 모바일 환경에서의 폭 */
    }
  }
`;

const TableContent = styled.div`
  white-space: pre-wrap; /* 줄바꿈과 공백 유지 */
  word-wrap: break-word; /* 단어 단위로 줄바꿈 */
  word-break: break-word; /* 긴 단어도 강제 줄바꿈 */
  padding: 10px;
  background-color: #ebf0ee;
  min-height: 410px;
`;

const ButtonContainer = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

function MyPageTab1() {
  const [inquiry, setInquiry] = useState<InquiryListData[]>([]);
  const [permissions, setPermissions] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryData | null>(
    null
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(0); //
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  const handleRowClick = async (index: number, inquiryId: string) => {
    if (permissions) {
      try {
        //console.log('클릭함');
        let response;
        if (permissions === 'BUYER') {
          response = await BuyerInquiryService.getInquiryDetail(inquiryId);
        } else if (permissions === 'SELLER') {
          response = await SellerInquiryService.getInquiryDetail(inquiryId);
        }

        if (!response || !response.data) {
          console.error('API Response is undefined or invalid');
          return;
        }

        //console.log('API Response:', response.data);
        setSelectedInquiry(response.data);

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('questionId', inquiryId);
        window.history.pushState({}, '', currentUrl);

        setSelectedIndex(index);
      } catch (error) {
        console.error('공지사항 상세 조회 실패:', error);
      }
    }
  };
  const handleButtonClick = async () => {
    setSelectedInquiry(null); // 선택된 공지사항 데이터 저장

    // URL 초기화 (페이지 이동 없이)
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('questionId');
    window.history.pushState({}, '', currentUrl);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchInquiry(page);
  };

  useEffect(() => {
    const storedPermissions = localStorage.getItem('permissions') || 'default';
    setPermissions(storedPermissions);
  }, []);

  useEffect(() => {
    if (permissions) {
      const urlParams = new URLSearchParams(window.location.search);
      const questionId = urlParams.get('questionId');
      //console.log('questionId from URL:', questionId);

      if (questionId) {
        handleRowClick(0, questionId);
        //console.log(questionId);
      }
      fetchInquiry(0);
    }
  }, [permissions]); // permissions가 변경될 때만 fetchInquiry 호출

  const fetchInquiry = async (currentPage: number) => {
    try {
      if (permissions === 'BUYER') {
        const response = await BuyerInquiryService.getInquiriesList(
          currentPage,
          12
        );
        setInquiry(response.data.content);
        setTotalPages(response.data.totalPages);
      } else if (permissions === 'SELLER') {
        const response = await SellerInquiryService.getInquiriesList(
          currentPage,
          12
        );
        setInquiry(response.data.content);
        setTotalPages(response.data.totalPages);
      }
    } catch (err) {
      console.error(err);
      alert(err);
    }
  };

  return (
    <NoticeTabWrapper>
      <NoticeHeader>
        <img src={leaf} alt="leaf" />
        <h1>
          {permissions === 'SELLER'
            ? '판매자 문의 이력'
            : permissions === 'BUYER'
              ? '나의 문의 이력'
              : ''}
        </h1>
      </NoticeHeader>
      {selectedInquiry ? (
        <>
          <TableContainer id="table-container">
            <TableBox>
              <Table>
                <thead>
                  <tr>
                    <th>NO</th>
                    <th>차종</th>
                    <th>제조사</th>
                    <th>고유번호</th>
                    {permissions && (
                      <th>
                        {permissions === 'SELLER'
                          ? '문의자정보'
                          : permissions === 'BUYER'
                            ? '판매자정보'
                            : ''}
                      </th>
                    )}
                    <th>작성시간</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{selectedIndex + 1}</td>
                    <td>{selectedInquiry.carModel}</td>
                    <td>{selectedInquiry.carManufacturer}</td>
                    <td>{selectedInquiry.identifier}</td>
                    <td>{selectedInquiry.memberName}</td>
                    <td>{formatDateWithTime(selectedInquiry.regDate)}</td>
                  </tr>
                </tbody>
              </Table>
            </TableBox>
            <TableContent>{selectedInquiry.content}</TableContent>
          </TableContainer>
          <ButtonContainer>
            <Button save={true} onClick={() => handleButtonClick()}>
              목록
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <>
          <TableContainer>
            <TableBox>
              <Table>
                <thead>
                  <tr>
                    <th>NO</th>
                    <th>차종</th>
                    <th>제조사</th>
                    <th>고유번호</th>
                    <th>
                      {permissions === 'SELLER'
                        ? '구매자 정보'
                        : permissions === 'BUYER'
                          ? '판매자 정보'
                          : ''}
                    </th>
                    <th>작성시간</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiry.length === 0 && (
                    <tr>
                      <td colSpan={6}>문의이력이 없습니다.</td>
                    </tr>
                  )}
                  {inquiry.map((item, index) => (
                    <tr
                      key={item.inquiryId}
                      onClick={() => handleRowClick(index, item.inquiryId)}
                    >
                      <td>{index + 1}</td>
                      <td>{item.carModel}</td>
                      <td>{item.carManufacturer}</td>
                      <td>{item.identifier}</td>
                      <td>{item.memberName}</td>
                      <td>{formatDateWithTime(item.regDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableBox>
          </TableContainer>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </NoticeTabWrapper>
  );
}

export default MyPageTab1;
